import React, { useEffect, useState } from 'react'
import { useNavigate, useParams, useLocation, Navigate, Link } from 'react-router-dom'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import muiTheme from '../../config/muiTheme'
import {
	EuiButton,
	EuiFlexGroup,
	EuiFlexItem,
	EuiPageHeader,
	EuiTitle,
} from '@equipmentshare/ds2'
import { heapEvent } from '@equipmentshare/heaper'
import {
	Permission,
	TransferOrderStatus,
	useTransferOrderQuery,
	useReceiveTransferOrderLineItemsMutation,
} from '../../graphql'
import { useShowError, useShowSuccess } from 'hooks/useToast'
import PermissionsCheck from '../../components/PermissionsCheck'
import { LAYOUT_CONSTANTS, Z_INDEX } from '../requests/constants/layoutConstants'

const RECEIVABLE_STATUSES = new Set( [
	TransferOrderStatus.Approved,
	TransferOrderStatus.InTransit,
] )

const STATUS_RECEIVE_GUARD: Partial<Record<TransferOrderStatus, string>> = {
	[ TransferOrderStatus.Pending ]: 'This transfer order is still pending approval and cannot be received yet.',
	[ TransferOrderStatus.Rejected ]: 'This transfer order was rejected and cannot be received.',
	[ TransferOrderStatus.Completed ]: 'This transfer order has already been fully received.',
	[ TransferOrderStatus.Cancelled ]: 'This transfer order has been cancelled and cannot be received.',
}

export default function ReceiveTransferOrder() {
	const { id } = useParams<{ id: string }>()
	const navigate = useNavigate()
	const location = useLocation()
	const showSuccess = useShowSuccess()
	const showError = useShowError()

	const { data, loading, error } = useTransferOrderQuery( {
		variables: { transferOrderId: id ?? '' },
		skip: !id,
		fetchPolicy: 'network-only',
	} )

	const [ quantities, setQuantities ] = useState<Record<string, number>>( {} )
	const [ receiveTransferOrderLineItems, { loading: submitting } ] = useReceiveTransferOrderLineItemsMutation()

	const order = data?.transferOrder

	useEffect( () => {
		if ( id ) {
			heapEvent( 'Inventory - Transfer Orders - Receive - Page - Open', { transfer_order_id: id } )
		}
	}, [ id ] )

	const handleBackClick = () => {
		if ( location.key !== 'default' ) {
			navigate( -1 )
		} else {
			navigate( `/transfer-orders/${id}` )
		}
	}

	const receivableItems = ( order?.lineItems ?? [] ).filter( ( item ) => {
		const remaining = item.quantityRequested - ( item.quantityReceived ?? 0 )
		return remaining > 0
	} )

	const handleQuantityChange = ( lineItemId: string, value: string ) => {
		const parsed = parseInt( value, 10 )
		setQuantities( ( prev ) => ( {
			...prev,
			[ lineItemId ]: isNaN( parsed ) ? 0 : parsed,
		} ) )
	}

	const hasAnyQuantity = Object.values( quantities ).some( ( q ) => q > 0 )

	const hasInvalidQuantity = receivableItems.some( ( item ) => {
		const remaining = item.quantityRequested - ( item.quantityReceived ?? 0 )
		const qty = quantities[ item.lineItemId ] ?? 0
		return qty < 0 || qty > remaining
	} )

	const handleSubmit = async() => {
		if ( !id || !hasAnyQuantity || hasInvalidQuantity ) {
			return
		}

		const lineItemsToReceive = receivableItems
			.filter( ( item ) => ( quantities[ item.lineItemId ] ?? 0 ) > 0 )
			.map( ( item ) => ( {
				lineItemId: item.lineItemId,
				quantityReceived: quantities[ item.lineItemId ],
			} ) )

		try {
			const result = await receiveTransferOrderLineItems( {
				variables: {
					input: {
						transferOrderId: id,
						lineItems: lineItemsToReceive,
					},
				},
			} )

			if ( result.data?.receiveTransferOrderLineItems?.success ) {
				heapEvent( 'Inventory - Transfer Orders - Receive - Submit - Success', { transfer_order_id: id } )
				showSuccess( `receiveTransferOrderSuccess${Date.now()}`, result.data.receiveTransferOrderLineItems.message || 'Items received successfully.' )
				navigate( `/transfer-orders/${id}` )
			} else {
				const message = result.data?.receiveTransferOrderLineItems?.message ?? 'Failed to receive items.'
				heapEvent( 'Inventory - Transfer Orders - Receive - Submit - Error', { transfer_order_id: id } )
				showError( `receiveTransferOrderError${Date.now()}`, message )
			}
		} catch ( err ) {
			heapEvent( 'Inventory - Transfer Orders - Receive - Submit - Error', { transfer_order_id: id } )
			const message = err instanceof Error ? err.message : 'An unexpected error occurred.'
			showError( `receiveTransferOrderError${Date.now()}`, message )
		}
	}

	return (

		<PermissionsCheck
			permissionsToCheck={[ Permission.CanCompletePartsTransferByStoreId ]}
			renderNoAccess={() => <Navigate to="/transfer-orders" replace />}
		>
			<Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
				{/* Scrollable content */}
				<Box sx={{ flex: 1, overflowY: 'auto', pb: `${LAYOUT_CONSTANTS.contentBottomPadding}px` }}>
					{/* Page Header */}
					<EuiPageHeader
						pageTitle={<EuiTitle size="m"><span>Receive Items</span></EuiTitle>}
						paddingSize="l"
						bottomBorder="extended"
					/>

					<Box sx={{ p: 3 }}>
						{/* Loading */}
						{loading && (
							<Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
								<CircularProgress />
							</Box>
						)}

						{/* Fetch error */}
						{( error || ( !loading && !order ) ) && (
							<Alert
								severity="error"
								action={
									<Button color="inherit" size="small" onClick={handleBackClick}>
										Back
									</Button>
								}
							>
								{error?.message ?? 'Transfer order not found.'}
							</Alert>
						)}

						{/* Status guard */}
						{order && !RECEIVABLE_STATUSES.has( order.status ) && (
							<Alert
								severity="warning"
								action={
									<Button color="inherit" size="small" component={Link} to={`/transfer-orders/${id}`}>
										Back to Transfer Order
									</Button>
								}
							>
								{STATUS_RECEIVE_GUARD[ order.status ] ?? 'This transfer order cannot be received in its current status.'}
							</Alert>
						)}

						{/* Content */}
						{order && RECEIVABLE_STATUSES.has( order.status ) && (
							<Stack spacing={3}>
								<Paper variant="outlined" sx={{ p: 2 }}>
									<Stack direction="row" spacing={2} alignItems="center">
										<Typography>
											<strong>From:</strong> {order.fromStore?.name}
										</Typography>
										<Box sx={{ color: 'text.secondary' }}>→</Box>
										<Typography>
											<strong>To:</strong> {order.toStore?.name}
										</Typography>
									</Stack>
								</Paper>

								{receivableItems.length === 0 ? (
									<Alert severity="info">All line items have been fully received.</Alert>
								) : (
									<Box>
										<Typography variant="h6" gutterBottom>
											Line Items
										</Typography>
										<TableContainer component={Paper} variant="outlined">
											<Table size="small">
												<TableHead>
													<TableRow>
														<TableCell>MFR #</TableCell>
														<TableCell>Manufacturer</TableCell>
														<TableCell>Description</TableCell>
														<TableCell align="right">Qty Requested</TableCell>
														<TableCell align="right">Qty Received</TableCell>
														<TableCell align="right">Qty Remaining</TableCell>
														<TableCell align="right">Qty to Receive</TableCell>
													</TableRow>
												</TableHead>
												<TableBody>
													{receivableItems.map( ( item ) => {
														const alreadyReceived = item.quantityReceived ?? 0
														const remaining = item.quantityRequested - alreadyReceived
														const qty = quantities[ item.lineItemId ] ?? 0
														const isOverMax = qty > remaining
														return (
															<TableRow key={item.lineItemId}>
																<TableCell>{item.product?.manufacturerNumber ?? '—'}</TableCell>
																<TableCell>{item.product?.manufacturer?.name ?? '—'}</TableCell>
																<TableCell>{item.product?.name ?? item.productId}</TableCell>
																<TableCell align="right">{item.quantityRequested}</TableCell>
																<TableCell align="right">{alreadyReceived}</TableCell>
																<TableCell align="right">{remaining}</TableCell>
																<TableCell align="right" sx={{ width: 140 }}>
																	<TextField
																		type="number"
																		size="small"
																		value={qty}
																		onChange={( e ) => handleQuantityChange( item.lineItemId, e.target.value )}
																		error={isOverMax}
																		helperText={isOverMax ? `Max ${remaining}` : undefined}
																		sx={{ width: 110 }}
																	/>
																</TableCell>
															</TableRow>
														)
													} )}
												</TableBody>
											</Table>
										</TableContainer>
									</Box>
								)}
							</Stack>
						)}
					</Box>
				</Box>

				{/* Sticky Footer */}
				<Box
					sx={{
						position: 'fixed',
						bottom: 0,
						left: 0,
						right: 0,
						backgroundColor: 'formPanel.footerBackground',
						padding: `${LAYOUT_CONSTANTS.footerPaddingVertical}px ${LAYOUT_CONSTANTS.footerPaddingHorizontal}px`,
						boxShadow: muiTheme.palette.formPanel.shadow,
						zIndex: Z_INDEX.stickyFooter,
					}}
				>
					<EuiFlexGroup gutterSize="m" justifyContent="flexEnd">
						<EuiFlexItem grow={false} style={{ minWidth: LAYOUT_CONSTANTS.minCancelButtonWidth }}>
							<EuiButton
								color="text"
								style={{
									backgroundColor: 'transparent',
									border: `1px solid ${muiTheme.palette.common.white}`,
									color: muiTheme.palette.common.white,
								}}
								onClick={handleBackClick}
								disabled={submitting}
							>
								Cancel
							</EuiButton>
						</EuiFlexItem>
						<EuiFlexItem grow={false} style={{ minWidth: LAYOUT_CONSTANTS.minSubmitButtonWidth }}>
							<EuiButton
								fill
								onClick={handleSubmit}
								isLoading={submitting}
								disabled={!hasAnyQuantity || hasInvalidQuantity || submitting}
							>
								Receive Items
							</EuiButton>
						</EuiFlexItem>
					</EuiFlexGroup>
				</Box>
			</Box>
		</PermissionsCheck>
	)
}
